import { POINTS_RACES_ROUTE_RULE } from "../console/pages/races/racesNav";
import { DEVELOPER_ROUTE_RULE } from "../console/pages/developer/developerNav";

export interface AdminFeatureRouteRule {
  /**
   * The feature that grants this route. Where a section is reachable by more
   * than one entitlement, list them all — ANY ONE grants access.
   *
   * Communications is the reason this is a list: the legacy /portal page hosts
   * campaign tools, while the newsroom CMS under it answers to `content.*`. A
   * single key sent a content editor to /unauthorized before the page could
   * explain which permission was actually missing.
   */
  featureKeys: string[];
  patterns: string[];
}

/**
 * The Money domain's READ entitlements. Either key opens a money read surface
 * (transactions, the transaction detail, pay-ins, the payout consoles and the
 * unattributed queue): `admin.finance.view` is what finance operators hold,
 * `money.read` is the read-only capability the support roles hold (ENG-536).
 *
 * This is deliberately a READ-only pairing: the WRITE actions on those pages
 * (approve/reject a transaction, resolve a payout, attribute an unattributed
 * deposit) stay gated on `admin.finance.view` alone, so a `money.read`-only
 * operator can see everything but change nothing. Markets, Trading, Ledger and
 * Products are NOT money-read surfaces and keep `admin.finance.view` on their
 * own rule below.
 */
export const MONEY_READ_KEYS = ["admin.finance.view", "money.read"];

export const ADMIN_FEATURE_ROUTE_RULES: AdminFeatureRouteRule[] = [
  // DPOS sessions console — more specific than the catch-all /console/* dashboard
  // rule below, so it must come first (first matching rule wins).
  {
    featureKeys: ["dpos.read"],
    patterns: [
      "/console/dpos",
      "/console/dpos/*",
      // Legacy-admin DPOS sessions oversight (same capability as the console).
      // Exact + slash-scoped subpaths only, so a trailing-glob can't also match
      // unrelated siblings like /portal/dpos-sessions-anything.
      "/portal/dpos-sessions",
      "/portal/dpos-sessions/*",
    ],
  },
  // ZAR8 product consoles (treasury, conversions, reserves, rewards, yield,
  // proof-of-reserves) plus AutoStack and Brokers oversight — the finance
  // capability, matching the legacy ZAR8/autostack/broker pages. More specific
  // than the catch-all /console/* rule below, so it must come first (first
  // matching rule wins).
  {
    featureKeys: ["admin.finance.view"],
    patterns: [
      "/console/products/zar8-*",
      "/console/products/autostack",
      "/console/products/autostack/*",
      "/console/products/brokers",
      "/console/products/brokers/*",
    ],
  },
  // The business @handle applications queue (T-312). Its own capability, held
  // by founder, super and compliance: approving a business's handle is a
  // brand-impersonation call, and the API gates every route behind the queue
  // on `admin.business_handles`, not on the support desk's user.edit.
  // MUST precede the Businesses rule below, whose /console/businesses/* would
  // otherwise claim it (first matching rule wins).
  {
    featureKeys: ["admin.business_handles"],
    patterns: ["/console/businesses/handles", "/console/businesses/handles/*"],
  },
  // Business onboarding: the case queue and the case wizard. compliance.review,
  // the capability every /admin/onboarding route requires; a user.edit-only
  // operator could open the page and then be refused every read on it. MUST
  // precede the Businesses rule below (first matching rule wins).
  {
    featureKeys: ["compliance.review"],
    patterns: [
      "/console/businesses/onboarding",
      "/console/businesses/onboarding/*",
    ],
  },
  // Businesses domain (register + KYB account requests) and the Customers
  // PIN-reset queue — the same capability as the legacy /portal pages. The old
  // /console/customers/businesses|organisations paths stay listed because they
  // still route (as redirects into /console/businesses*).
  {
    featureKeys: ["user.edit"],
    patterns: [
      "/console/businesses",
      "/console/businesses/*",
      "/console/customers/businesses",
      "/console/customers/businesses/*",
      "/console/customers/organisations",
      "/console/customers/organisations/*",
      "/console/customers/pin-resets",
      "/console/customers/pin-resets/*",
    ],
  },
  // Acquisition analytics (/console/customers/acquisition) — a read-only surface
  // over per-user attribution records, including a drill-down listing the signups
  // behind each breakdown. It carries audit.view, the same read capability as the
  // audit log and login history, NOT the Customers domain's user.edit write key.
  // More specific than the catch-all /console/* rule below, so it must come first.
  {
    featureKeys: ["audit.view"],
    patterns: [
      "/console/customers/acquisition",
      "/console/customers/acquisition/*",
    ],
  },
  // Customer insights (/console/customers/insights) — the whole base in aggregate.
  // Read-only over per-user records, so audit.view like Acquisition. Must also
  // come before the /console/* catch-all.
  {
    featureKeys: ["audit.view"],
    patterns: ["/console/customers/insights", "/console/customers/insights/*"],
  },
  // Banks directory — a configuration surface (legacy /portal/banks), so it
  // carries the settings capability, not the finance one.
  {
    featureKeys: ["system.settings"],
    patterns: [
      "/console/money/banks",
      "/console/money/banks/*",
      "/console/money/provider-readiness",
      "/console/money/provider-readiness/*",
    ],
  },
  // Asset configuration surfaces (display order, NCW assets/blockchains) —
  // same capability as the legacy /portal asset pages.
  {
    featureKeys: ["asset.flag.flip"],
    patterns: [
      "/console/assets/order",
      "/console/assets/order/*",
      "/console/assets/ncw",
      "/console/assets/ncw/*",
      "/console/assets/ncw-blockchains",
      "/console/assets/ncw-blockchains/*",
    ],
  },
  // KYC/KYB provider routing (the compliance settings surface) and the KYB
  // submissions queue + review packet — all compliance.review, mirroring the
  // pages' own hard gates.
  {
    featureKeys: ["compliance.review"],
    patterns: [
      "/console/compliance/provider-routing",
      "/console/compliance/provider-routing/*",
      "/console/compliance/kyb",
      "/console/compliance/kyb/*",
      // KYC compliance cases: compliance-only, never the complaints-only reach
      // the domain catch-all below would give it.
      "/console/compliance/cases",
      "/console/compliance/cases/*",
    ],
  },
  // The regulatory complaint register (FSCA / CBUAE) reads its OWN capability,
  // NOT compliance.review — `compliance.complaint.view` is deliberately held by
  // the support cohort too, because the agent working a ticket is the first to
  // spot that it is a formal complaint. Folding it into the broader
  // /console/compliance/* rule below would lock support and support_lead out of
  // exactly the surface that capability exists to give them.
  //
  // It also needs a rule at all: it previously had NONE, so the register was
  // readable by any admin via a direct URL — the menus gated it but the route
  // did not, and the page itself only gates its export.
  {
    featureKeys: ["compliance.complaint.view"],
    patterns: [
      "/console/compliance/complaints",
      "/console/compliance/complaints/*",
    ],
  },
  // Handle watch (ENG-976/977): the queue of handles that may misrepresent
  // somebody, each flag, and revoking / reallocating a person's handle. Its own
  // capability, held by founder, super and compliance — the API gates every
  // route on `admin.handle_watch`, not on compliance.review. MUST precede the
  // /console/compliance/* catch-all below (first matching rule wins).
  {
    featureKeys: ["admin.handle_watch"],
    patterns: [
      "/console/compliance/handle-watch",
      "/console/compliance/handle-watch/*",
    ],
  },
  // Travel Rule queue: crypto withdrawals the Travel Rule check holds. Read by
  // compliance, who release holds, and by whoever approves withdrawals, who
  // need to see why one is held — the API's own read gate, either capability.
  // MUST precede the /console/compliance/* catch-all below, which would let a
  // complaints-only operator in.
  {
    featureKeys: ["compliance.review", "withdrawal.approve"],
    patterns: [
      "/console/compliance/travel-rule",
      "/console/compliance/travel-rule/*",
    ],
  },
  // PayLinks oversight console — the same finance capability as the other
  // payments surfaces (legacy 88Pay PaymentTransaction, ZAR8 treasury). More
  // specific than the catch-all /console/* rule below, so it must come first.
  {
    featureKeys: ["admin.finance.view"],
    patterns: ["/console/money/paylinks", "/console/money/paylinks/*"],
  },
  // The referral campaign register — what each campaign has been allocated,
  // committed and has left, plus who a spent budget is currently refusing. A
  // money READ surface: the API gates it on the same pair, so the support roles
  // reach it read-only via `money.read` without the authority to fund anything.
  // Declared explicitly even though the /console/money/* catch-all below would
  // already cover it, for the same legibility reason PayLinks and Payouts are.
  {
    featureKeys: MONEY_READ_KEYS,
    patterns: [
      "/console/money/referral-campaigns",
      "/console/money/referral-campaigns/*",
    ],
  },
  // Onafriq payout consoles — the needs-a-human queue, four-eyes approvals, the
  // funding float, corridors/flags and the per-payment investigation. A money
  // READ surface, so the support roles reach it read-only via `money.read`
  // (ENG-536); the resolve/approve actions on these pages stay finance-gated.
  // /console/money/* below already resolves to the same keys, but the explicit
  // rule keeps route, nav and page in agreement the way the PayLinks rule does.
  // The payout release queue reads on the API's QUEUE gate — withdrawal.view or
  // money.read (routes/admin/payoutQueue.ts) — not the treasury pair, so an
  // operator who works withdrawals but holds no finance view still reaches it.
  // MUST sit above the /console/money/payouts/* rule below: first match wins.
  {
    featureKeys: ["withdrawal.view", "money.read"],
    patterns: ["/console/money/payouts/queue"],
  },
  {
    featureKeys: MONEY_READ_KEYS,
    patterns: ["/console/money/payouts", "/console/money/payouts/*"],
  },
  // QA coaching — the copilot quality-score review (/console/support/coaching).
  // A LEAD surface, so it carries support.manage rather than the desk's
  // support.view. It MUST sit above the /console/support* rule below: first match
  // wins, and /console/support/* would otherwise resolve it to support.view and
  // let a read-only agent open the coaching view.
  {
    featureKeys: ["support.manage"],
    patterns: ["/console/support/coaching", "/console/support/coaching/*"],
  },
  // Knowledge base (/console/support/knowledge). The desk reads it with
  // support.view; compliance reaches it with faq.compliance.approve to work the
  // copy review queue (ENG-1054) without being handed the support desk. MUST sit
  // above the /console/support* rule below: first match wins.
  {
    featureKeys: ["support.view", "faq.compliance.approve"],
    patterns: ["/console/support/knowledge", "/console/support/knowledge/*"],
  },
  // Support console — its own domain capability, so support agents can work the
  // desk without the wider admin surface. More specific than the catch-all
  // /console/* rule below, so it must come first.
  {
    featureKeys: ["support.view"],
    patterns: ["/console/support", "/console/support/*"],
  },

  // ── Console domain roots ────────────────────────────────────────────────────
  // These used to fall through to the `admin.dashboard` catch-all below, which
  // every console-capable admin holds — so they were not gated at all, and the
  // sidebar (which resolves its gates from these rules) showed all 15 domains to
  // everyone. Each rule names the capability that already governs the domain's
  // own subpages, so nav and route now agree instead of drifting.
  //
  // NOTE these sit AFTER the narrower rules above (e.g. /console/money/banks →
  // system.settings) — first match wins, so the specific ones still take
  // precedence.

  // The money surface — transactions, the transaction detail, pay-ins and the
  // unattributed queue. A READ surface for the support roles: either
  // `admin.finance.view` (finance operators) or `money.read` (support, ENG-536)
  // opens it, and the pages hide their write actions from a money.read-only
  // operator. Markets, the trading desk, the ledger and the product consoles are
  // NOT money-read surfaces — they keep `admin.finance.view` on the rule below.
  {
    featureKeys: MONEY_READ_KEYS,
    patterns: ["/console/money", "/console/money/*"],
  },
  // Market operations, the trading desk, the ledger/solvency views and the
  // product consoles all read the same finance capability their legacy /portal
  // equivalents do. NOT opened to `money.read`: support gets the money surface,
  // not the trading and product desks.
  {
    featureKeys: ["admin.finance.view"],
    patterns: [
      "/console/markets",
      "/console/markets/*",
      "/console/trading",
      "/console/trading/*",
      "/console/finance",
      "/console/finance/*",
      "/console/products",
      "/console/products/*",
    ],
  },
  // The Compliance domain ROOT takes either entitlement, and that matters: the
  // KYC queue page renders a "you don't have KYC review" panel that still offers
  // the Complaint Register to a complaints-only operator. Gating the root on
  // compliance.review alone would bounce them at the router before the page could
  // do that — the landing page IS the explanation, so it has to stay reachable by
  // anyone holding any compliance-family capability. The narrower subpages above
  // (kyb, provider-routing, complaints) keep their own single keys.
  {
    featureKeys: ["compliance.review", "compliance.complaint.view"],
    patterns: ["/console/compliance", "/console/compliance/*"],
  },
  // Asset catalogue, pairs, the creation wizard and the route builder — the same
  // kill-switch capability the asset-order and NCW subpages already require.
  {
    featureKeys: ["asset.flag.flip"],
    patterns: ["/console/assets", "/console/assets/*"],
  },
  // The newsletter list. Deliberately ABOVE the Communications rule below — rules are
  // first-match — because that rule also admits `comms.broadcast`, and the API serves
  // this list on `content.view` alone. Without this a broadcast-only operator would
  // reach the page and meet a 403 instead of the page that names what they lack.
  {
    featureKeys: ["content.view"],
    patterns: [
      "/console/communications/newsletter",
      "/console/communications/newsletter/*",
    ],
  },
  // The newsroom/marketing workspace. Either entitlement opens it: `content.view`
  // for the CMS, `comms.broadcast` for the campaign tools — mirroring the
  // /portal/communications rule further down.
  {
    featureKeys: ["content.view", "comms.broadcast"],
    patterns: ["/console/communications", "/console/communications/*"],
  },
  /**
   * Website — the marketing site's own domain (ADMIN_WEBSITE_DOMAIN_SPEC.md).
   *
   * The SAME two keys as Communications, deliberately: the surfaces this
   * domain will absorb are the ones that rule already governs, so on day one
   * nobody gains or loses reach — the board is a new view of pages they could
   * already open. Narrowing it to `content.view` alone would quietly lock out
   * every campaign operator the moment the moves land.
   *
   * The board's rows are READ-ONLY projections of what the public site serves,
   * and each row's action re-checks the OWNING surface's key before offering a
   * jump — this rule opens the board, not the editors behind it.
   */
  {
    featureKeys: ["content.view", "comms.broadcast"],
    patterns: ["/console/website", "/console/website/*"],
  },
  /**
   * Company — the legal record (entities, FSP licences, data-protection
   * contacts, jurisdictions, deposit accounts) and the marketing site's
   * settings, as two tabs of one hub.
   *
   * EITHER key opens it, and that is the point: the tabs answer to different
   * capabilities, so an intersection would hide the domain from people who
   * should reach half of it, and a single key would hide the half they hold.
   * `system.settings` is the console's configuration key — the same one Banks
   * and provider-readiness use; `content.view` is what the site-settings GET
   * demands. The hub gates each tab individually on the same two.
   *
   * 🔴 THIS IS A REAL CHANGE, not a like-for-like move. "Legal & brand" lived
   * in System, which deliberately sits on the `admin.dashboard` operator-level
   * rule, so every console admin could open it. An operator holding neither key
   * loses a menu item they can see today — which is the intended correction:
   * the registered addresses and licence numbers on this page are what invoices
   * and the website's regulatory disclosure are drawn from.
   */
  {
    featureKeys: ["system.settings", "content.view"],
    patterns: ["/console/company", "/console/company/*"],
  },

  // AI8 — the support copilot admin hub. Its own read capability (`ai8.view`),
  // seeded server-side separately; declared here so the domain root is gated
  // rather than falling through to the `admin.dashboard` catch-all (which would
  // show the sidebar entry to every operator). More specific than /console/*, so
  // it must come first.
  {
    featureKeys: ["ai8.view"],
    patterns: ["/console/ai8", "/console/ai8/*"],
  },

  // Growth › Points races — opened on campaign.points.view; each write checks its own key.
  POINTS_RACES_ROUTE_RULE,

  // Customers › Developer — the developer portal's workspaces and invitations,
  // opened on developer.view; inviting and suspending check their own keys.
  DEVELOPER_ROUTE_RULE,

  // Deliberately NOT given a narrower rule, so they keep falling through to the
  // `admin.dashboard` "is an operator" gate below:
  //   /console            the landing work queue — every operator lands here
  //   /console/customers  every operational role legitimately looks a customer
  //                       up; the PII itself is gated server-side by
  //                       customer.pii.view + the access-grant lease, so gating
  //                       the LIST as well would block finance from a payment
  //                       investigation without protecting anything extra
  //   /console/approvals  a shared maker-checker queue; adminApprovalService
  //                       enforces per-record who may actually decide
  //   /console/system     its tabs answer to DIFFERENT capabilities (audit.view,
  //                       role.grant, system.settings). A hub-level gate would
  //                       deny people tabs they should reach — this needs
  //                       per-tab gating, not a domain rule
  //   /console/health     read-only operational telemetry
  {
    featureKeys: ["admin.dashboard"],
    patterns: ["/portal/dashboard", "/console", "/console/*"],
  },
  {
    featureKeys: ["user.edit"],
    patterns: [
      "/portal/clients",
      "/portal/clientuser*",
      "/portal/clientbalanceoverview*",
      "/portal/clientbankaccounts*",
      "/portal/client-usd-bankaccounts*",
      "/portal/client-withdrawall-addresses*",
      "/portal/client-api-keys*",
      "/portal/organisations*",
      "/portal/businesses*",
      "/portal/pin-reset-requests*",
      "/portal/manage-tickets*",
      "/portal/manage-faqs*",
    ],
  },
  {
    featureKeys: ["compliance.review"],
    patterns: ["/portal/verif-clients*"],
  },
  {
    featureKeys: ["admin.finance.view"],
    patterns: [
      "/portal/PaymentTransaction*",
      "/portal/autostack*",
      "/portal/autostack-purchases*",
      "/portal/user-autostacks*",
      "/portal/deposits*",
      "/portal/withdrawals*",
      "/portal/crypto-deposits*",
      "/portal/crypto-withdrawals*",
      "/portal/unattributed-deposits*",
      "/portal/deposit-logs*",
      "/portal/withdrawal-logs*",
      "/portal/tx-logs*",
      "/portal/arbitrage-batches*",
      "/portal/liquidity-pool*",
      "/portal/lp-history*",
      "/portal/lp-alert*",
      "/portal/trading-fees*",
      "/portal/transaction-history*",
      "/portal/custom-orders*",
      "/portal/deferred-settlements*",
      "/portal/broker-applications*",
      "/portal/broker-overview*",
      "/portal/broker-trades*",
      "/portal/broker-commissions*",
      "/portal/manage-investments*",
      "/portal/subscription-approval*",
      "/portal/user-summary*",
      "/portal/payouts*",
      "/portal/zar8-conversions*",
      "/portal/zar8-reserves*",
      "/portal/zar8-rewards*",
      "/portal/zar8-yield-positions*",
      "/portal/zar8-yield-distributions*",
      "/portal/zar8-yield-configuration*",
      "/portal/zar8-proof-of-reserves*",
    ],
  },
  {
    featureKeys: ["asset.flag.flip"],
    patterns: [
      "/portal/trading-assets*",
      "/portal/asset-order*",
      "/portal/tradable-pairs*",
      "/portal/tradeble-pairs*",
      "/portal/ncw-supported-assets*",
      "/portal/ncw-supported-blockchain*",
      "/portal/chooseAsset*",
    ],
  },
  {
    // Policies follow the key the API demands on every policy write.
    featureKeys: ["content.policy.manage"],
    patterns: ["/portal/policies*"],
  },
  {
    featureKeys: ["system.settings"],
    patterns: [
      "/portal/all-news*",
      "/portal/notifications*",
      "/portal/emails*",
      "/portal/settings-system*",
      "/portal/settings-api-keys*",
      "/portal/banks*",
      "/portal/2fa*",
      "/portal/settings*",
    ],
  },
  // Communications hosts TWO capabilities that the API gates differently, so
  // either one opens the section and the screens inside it gate themselves:
  //
  //   content.view      the newsroom CMS — blog, press releases, media
  //                     coverage, podcast, social, press kit, press list.
  //                     This is what the API actually enforces on all of it.
  //   comms.broadcast   the campaign tools. A console-side convention: the
  //                     API gates those routes on adminWrite, not on this.
  //
  // Scope note, so this is not read as bigger than it is: the CONSOLE newsroom
  // lives under /console/* and is gated on admin.dashboard, so it was never
  // blocked by this rule — the in-page gates are what were wrong there. This
  // rule governs the legacy /portal/communications page, where listing only
  // comms.broadcast bounced a content editor to /unauthorized instead of
  // letting the page name the permission they lack.
  {
    featureKeys: ["content.view", "comms.broadcast"],
    patterns: ["/portal/communications*"],
  },
  {
    featureKeys: ["audit.view"],
    patterns: ["/portal/audit-logs*", "/portal/login-history*"],
  },
  { featureKeys: ["role.grant"], patterns: ["/portal/access-control*"] },
];

const patternMatchesPath = (pattern: string, pathname: string): boolean => {
  if (pattern.endsWith("*")) {
    const prefix = pattern.slice(0, -1);
    return pathname === prefix || pathname.startsWith(prefix);
  }

  if (pattern.includes(":")) {
    const escapedPattern = pattern
      .split("/")
      .map((segment) => {
        if (segment.startsWith(":")) {
          return "[^/]+";
        }

        return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      })
      .join("/");

    return new RegExp(`^${escapedPattern}$`).test(pathname);
  }

  return pathname === pattern;
};

/**
 * The features that grant this path — ANY ONE of them is enough.
 *
 * Returns `null` for a path no rule covers, which means "not feature-gated"
 * rather than "denied": callers must treat null as unrestricted, exactly as
 * they did when this returned a single key.
 */
export const getRequiredAdminFeaturesForPath = (
  pathname: string
): string[] | null => {
  for (const rule of ADMIN_FEATURE_ROUTE_RULES) {
    if (
      rule.patterns.some((pattern) => patternMatchesPath(pattern, pathname))
    ) {
      return rule.featureKeys;
    }
  }

  return null;
};

/**
 * @deprecated Use `getRequiredAdminFeaturesForPath`. Kept so a caller that has
 * not moved yet still gets a working answer for every single-key rule — but it
 * reports only the FIRST key, so on Communications it would deny a content
 * editor the section grants. Do not add new callers.
 */
export const getRequiredAdminFeatureForPath = (
  pathname: string
): string | null => getRequiredAdminFeaturesForPath(pathname)?.[0] ?? null;
